import { Flex, Spinner, Text } from "@chakra-ui/react";
import { ReactNode } from "react";

import { FormSegmentPicker } from "@/common/components/Form/FormSegmentPicker";
import { SegmentSupport } from "@/common/components/SegmentPicker";
import { TextWordBreak } from "@/common/components/TextWordBreak";
import { SegmentSizeIndicator } from "@/company/components/SegmentSizeIndicator";
import { useSegment } from "@/company/data/useSegments";
import {
  operatorDisplayName,
  relevantOperators,
} from "@reflag/shared/attributeFilter";
import { SegmentFilter } from "@reflag/shared/filter";
import { SegmentWithMetadataDTO } from "@reflag/shared/segmentAPI";

import { ManagedFormControl } from "../Form/ManagedFormControl";
import RuleOperator from "../Rule/RuleOperator";

type SegmentFilterItemProps = {
  filter: SegmentFilter;
  showIcon?: boolean;
  isLoading?: boolean;
  compact?: boolean;
  noWrap?: boolean;
};

export function SegmentFilterItem({
  filter,
  showIcon,
  isLoading,
  compact,
  noWrap,
}: SegmentFilterItemProps) {
  const { data: segment } = useSegment(filter.segmentId);
  return (
    <Flex align="center" display="inline-flex" whiteSpace="normal">
      {isLoading ? (
        <Flex align="center" as="span" color="dimmed" columnGap={2}>
          <Spinner size="xs" variant="subtle" />
          <TextWordBreak as="span" noWrap={noWrap}>
            Loading filter...
          </TextWordBreak>
        </Flex>
      ) : (
        <Flex
          align="center"
          as="span"
          columnGap={1.5}
          wrap={noWrap ? "nowrap" : "wrap"}
        >
          {!compact ? (
            <Text as="span" color="dimmed">
              {operatorDisplayName[filter.operator]}
            </Text>
          ) : compact && filter.operator === "NOT_SEGMENT" ? (
            <Text as="span" color="dimmed">
              not in
            </Text>
          ) : null}
          {segment && showIcon && (
            <SegmentSizeIndicator segmentId={segment.id} />
          )}
          {segment && <Text as="span">{segment.name}</Text>}
          {segment?.deletedAt && <Text color="dimmed">(archived)</Text>}
        </Flex>
      )}
    </Flex>
  );
}

type SegmentFilterFieldsProps = {
  segmentFilterFn?: (segment: SegmentWithMetadataDTO) => SegmentSupport;
  segmentUnsupportedReason?: ReactNode;
};

export function SegmentFilterFields({
  segmentFilterFn,
  segmentUnsupportedReason,
}: SegmentFilterFieldsProps) {
  const operators = relevantOperators(["segment"]);

  return (
    <>
      <ManagedFormControl
        name="operator"
        render={({ field }) => (
          <RuleOperator {...field} operators={operators} />
        )}
      />
      <FormSegmentPicker
        bg="appBackground"
        filterFn={segmentFilterFn}
        menuDescription="Pick a segment:"
        name="segmentId"
        showPieChart={true}
        unsupportedReason={segmentUnsupportedReason}
        w={"100%"}
      />
    </>
  );
}
